import React, { useEffect, useState } from 'react'
import connect from './connect'
import Loader from '../../../components/common/Loader'
import { Grid, Box } from '@material-ui/core'
import NothingFoundCard from '../../../components/generics/Cards/NothingFoundCard/NothingFoundCardList'
import PaginationSmall from '../../../components/common/Paginations/PaginationSmall'
import List from './components/List'
import Header from './components/Header'
import { emptyArray } from '../../../utils/arrayUtils'
import { Button, RouterLink } from '../../../components/common/Buttons'
import { useTrans } from '../../../services/i18n'
import Typography from '../../../components/common/Typography'
import { ANY_OF_THOSE, CONTAINS, IS_BETWEEN } from '../../../constants/filter'
import { useAlertContext } from '../../../contexts/AlertContext'
import { ModalDialog, DialogTitle } from '../../../components/common/Dialogs'
import useBooleanHandlers from '../../../hooks/useBooleanHandlers'

const SubmittedSubcontractorForms = ({
  subcontractorForms,
  onInit,
  onChangePage,
  onChangeRowPerPage,
  getStaffUsers,
  onChangeFilters,
  batchUpdateStatus,
  fetchList,
}) => {
  const { items, count, pending, pagination } = subcontractorForms
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const [selectedForms, setSelectedForms] = useState([])
  const [openDeleteConfirm, onOpenDeleteConfirm, onCloseDeleteConfirm] = useBooleanHandlers()

  useEffect(() => {
    getStaffUsers()
  }, [])

  useEffect(() => {
    // Fetch subcontractor forms on component mount
    onInit({ onlySubcontractorForms: true })
  }, [onInit])

  const resetFilters = () => {
    onChangeFilters({
      status: {
        type: ANY_OF_THOSE,
        value: ['pending', 'new', 'submitted'],
      },
      organizationName: {
        type: CONTAINS,
        value: '',
      },
      updatedAt: {
        type: IS_BETWEEN,
        value: {
          from: null,
          to: null,
        },
      },
    })
  }

  const handleSelectForm = (formId) => {
    setSelectedForms(prev => {
      if (prev.includes(formId)) {
        return prev.filter(id => id !== formId)
      }
      return [...prev, formId]
    })
  }

  const handleSelectAll = () => {
    if (selectedForms.length === items.length) {
      setSelectedForms([])
    } else {
      setSelectedForms(items.map(item => item.id))
    }
  }

  const handleBatchUpdateStatus = async (adminStatus) => {
    if (selectedForms.length === 0) {
      errorAlert(trans('no-forms-selected'))
      return
    }

    try {
      const forms = selectedForms.map(organizationFormId => ({
        organizationFormId,
        adminStatus
      }))

      const result = await batchUpdateStatus({ forms })

      if (result.payload.success) {
        successAlert(`${trans('status-updated-successfully')}: ${result.payload.results.success.length} worksheets`)
        setSelectedForms([])
        fetchList()
      }
    } catch (error) {
      errorAlert(trans('error-updating-status'))
    }
  }

  const handleDeleteClick = () => {
    if (selectedForms.length === 0) {
      errorAlert(trans('no-forms-selected'))
      return
    }
    onOpenDeleteConfirm()
  }

  const handleConfirmDelete = () => {
    handleBatchUpdateStatus('deleted')
    onCloseDeleteConfirm()
  }

  return (
    <>
      <Header />
      {pending && <Loader open={pending} />}
      {!emptyArray(items) ? (
        <Grid container spacing={2} direction="column">
          {selectedForms.length > 0 && (
            <Grid item xs zeroMinWidth>
              <Box display="flex" alignItems="center" p={2} style={{ gap: '12px' }}>
                <Typography variant="body1" weight="600" style={{ marginRight: '8px' }}>
                  {trans('selected')}: {selectedForms.length}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('progress')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#ffac00',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-in-progress')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleBatchUpdateStatus('completed')}
                  style={{
                    textTransform: 'none',
                    padding: '6px 16px',
                    backgroundColor: '#28a745',
                    color: '#fff'
                  }}
                >
                  {trans('mark-as-completed')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleDeleteClick}
                  style={{ textTransform: 'none', padding: '6px 16px' }}
                >
                  {trans('mark-as-deleted')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedForms([])}
                  style={{ textTransform: 'none', padding: '6px 16px' }}
                >
                  {trans('clear-selection')}
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item xs zeroMinWidth>
            <List
              items={items}
              selectedForms={selectedForms}
              onSelectForm={handleSelectForm}
              onSelectAll={handleSelectAll}
            />
          </Grid>

          <Grid item xs zeroMinWidth>
            <PaginationSmall
              page={pagination.page}
              perPage={pagination.perPage}
              count={count}
              onChangePage={onChangePage}
              onChangeRowPerPage={onChangeRowPerPage}
              pending={pending}
            />
          </Grid>
        </Grid>
      ) : !pending && emptyArray(items) ? (
        <Grid item xs zeroMinWidth>
          <NothingFoundCard>
            <Button component={RouterLink} onClick={resetFilters}>
              <Typography weight="400" variant="body1">
                {trans('clear-filters')}
              </Typography>
            </Button>
          </NothingFoundCard>
        </Grid>
      ) : null}

      <ModalDialog
        open={openDeleteConfirm}
        onClose={onCloseDeleteConfirm}
        DialogTitle={
          <DialogTitle
            title={trans('confirm-delete')}
            onClose={onCloseDeleteConfirm}
          />
        }
      >
        <Grid container spacing={3} direction="column">
          <Grid item>
            <Typography variant="body1">
              {trans('are-you-sure-delete-selected-requests')}
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="flex-end" style={{ gap: '12px' }}>
              <Button
                variant="outlined"
                onClick={onCloseDeleteConfirm}
              >
                {trans('cancel')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleConfirmDelete}
              >
                {trans('delete')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </ModalDialog>
    </>
  )
}

export default connect(SubmittedSubcontractorForms)
